import React, { useMemo } from "react";
import { Table } from "../../../../../shared/Table/Table";
import { useAppTranslation } from "../../../../../i18n/useAppTranslation";
import { useHeaderActions } from "./hooks/useHeaderActions";
import { useMembershipSavingsReport } from "./hooks/useMembershipSavingsReport";
import Header from "./components/Header";
import classes from "./scss/MembershipSavingsReport.module.scss";
import { Box } from "../../../../../shared/Box/Box";
import { Empty } from "../../../../../shared/Empty/Empty";
import { Skeleton } from "../../../../../shared/Skeleton/Skeleton";
import { ORDER_BY } from "./MembershipSavingsReport.utils";
import { SortAdornment } from "../../../../../shared/SortAdornment/SortAdornment";
import { LoadMoreObserver } from "../../../../../shared/LoadMoreObserver/LoadMoreObserver";
import { formatCurrency } from "../../../../../utilities/general";
import { CURRENCY } from "../../../../../consts/general";
import { useCurrentUserQuery } from "../../../../../api/queries/useUserQuery";

const MembershipSavingsReport = () => {
  const { tBi } = useAppTranslation.BusinessInsights();
  const { tCommon } = useAppTranslation.Common();
  const { selectedClinic, period, apiPeriod } = useHeaderActions();
  const { data: user } = useCurrentUserQuery();

  const requestParams = {
    start_date: apiPeriod.fromDate,
    end_date: apiPeriod.toDate,
    clinic_ids:
      selectedClinic.value.value !== 0 ? [selectedClinic.value.value] : null,
  };

  const {
    data,
    clinics,
    order,
    isLoading,
    isNextFetching,
    hasMorePage,
    fetchMore,
  } = useMembershipSavingsReport(requestParams);

  const tableDataEmpty = !isLoading && !data?.length;

  const tableData = useMemo(
    () =>
      data?.map((data) => ({
        patientName: data.patient,
        clinicName: data.clinic,
        membershipName: data.membership,
        membershipSavings: formatCurrency(
          data.membership_benefit,
          user?.currency || CURRENCY.usd,
        ),
      })),
    [data],
  );

  const clinicsOptions =
    useMemo(
      () =>
        clinics?.map((clinic) => ({
          label: clinic.clinic_name,
          value: clinic.id,
        })),
      [clinics],
    ) || [];

  const renderSortWrap = (children, field) => (
    <SortAdornment
      value={order.value.by === field ? order.value.direction : undefined}
      onToggle={() => order.update(field)}
    >
      {children}
    </SortAdornment>
  );

  return (
    <div className="flex-col font-14 gap-16">
      <Header
        title={tBi("membershipSavingsReport.label")}
        selectedClinic={selectedClinic}
        clinics={clinicsOptions}
        period={period}
        requestParams={{
          ...requestParams,
          sortBy: order.value.by,
          order: order.value.direction,
        }}
      />
      <div className="flex gap-16">
        <div className={classes.tableContainer}>
          <Table
            bgColor="white"
            cols={[
              {
                data: renderSortWrap(
                  tCommon("label.patientName"),
                  ORDER_BY.patientName,
                ),
                accessor: "patientName",
              },
              {
                data: renderSortWrap(
                  tCommon("label.clinic"),
                  ORDER_BY.clinicName,
                ),
                accessor: "clinicName",
              },
              {
                data: renderSortWrap(
                  tCommon("label.membershipName"),
                  ORDER_BY.membershipName,
                ),
                accessor: "membershipName",
              },
              {
                data: renderSortWrap(
                  tCommon("label.membershipSavings"),
                  ORDER_BY.membershipSavings,
                ),
                accessor: "membershipSavings",
              },
            ]}
            data={tableData}
          />
          {hasMorePage && (
            <LoadMoreObserver
              noPaddingBottom
              loadMore={fetchMore}
              isLoading={isNextFetching}
            />
          )}
          {isLoading && (
            <Box>
              <Skeleton count={8} height={40.5} borderRadius="0px" />
            </Box>
          )}
          {tableDataEmpty && (
            <Box>
              <Empty position="center" />
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};

export default MembershipSavingsReport;
